<script lang="ts">
  import "@fontsource/fira-mono/400.css";
  import "@fontsource/fira-mono/500.css";
  import "@fontsource/fira-mono/700.css";

  import "$app.css";
  import "../ansi.css";

  import { browser } from "$app/environment";
  import { beforeNavigate } from "$app/navigation";
  import { page, updated } from "$app/state";

  import { QueryClient, QueryClientProvider } from "@tanstack/svelte-query";
  import { Toaster } from "svelte-sonner";

  import { maybeShowCmdKHint } from "$lib/command-palette/cmdk-hint";
  import {
    effectiveTheme,
    resolvedTheme,
  } from "$lib/design-systems/product/foundations/theme";
  import type { LayoutProps } from "./$types";
  import Analytics from "./_components/Analytics.svelte";
  import Seo from "./_components/Seo.svelte";

  let { children }: LayoutProps = $props();

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { enabled: browser },
    },
  });

  const title = $derived(page.data.title || "Modal");

  // After version polling detects a deploy, load the new build on the next
  // pathname navigation. Query/hash-only navigation preserves page state.
  // https://svelte.dev/docs/kit/$app-navigation#beforeNavigate
  // https://svelte.dev/docs/kit/$app-state#updated
  beforeNavigate(({ from, to, willUnload, cancel }) => {
    if (
      updated.current &&
      !willUnload &&
      from &&
      to &&
      from.url.pathname !== to.url.pathname
    ) {
      cancel();
      window.location.assign(to.url.href);
    }
  });

  // Every route passes through this layout, so it owns `data-theme` for the
  // whole app. Re-runs on navigation so moving between a pinned surface and one
  // that follows the preference restamps the attribute.
  $effect(() => {
    document.documentElement.dataset.theme = effectiveTheme(
      page.route.id,
      $resolvedTheme,
    );
  });

  // When users copy text via keyboard (Cmd+C / Ctrl+C) the
  // navigator.clipboard.writeText path used by CopyButton isn't hit, so we
  // listen to the document-level `copy` event and inspect the active
  // selection to show the ⌘K hint for resolvable Modal IDs.
  $effect(() => {
    if (!browser) return;
    const onCopy = () => {
      maybeShowCmdKHint(window.getSelection()?.toString());
    };
    document.addEventListener("copy", onCopy);
    return () => document.removeEventListener("copy", onCopy);
  });
</script>

<svelte:head>
  <title>{title}</title>
</svelte:head>

<Seo
  title={page.data.ogTitle}
  description={page.data.ogDescription}
  imageUrl={page.data.ogImageUrl}
/>

<QueryClientProvider client={queryClient}>
  <Analytics />

  <Toaster />

  {@render children()}
</QueryClientProvider>
